'use client'

import type { NotificationTypeEnum } from '@maestro/commerce-blocks/components/notification/notification.types'
import type { Price } from '@maestro/data-layer'

import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { useNotificationContext } from '@maestro/commerce-blocks/components/notification/notification.context'

export const useCartNotifications = () => {
  const t = useTranslations('commerce-blocks')
  const { showInfo, showError, showUpdatedCart } = useNotificationContext()

  const notifyNotEnoughStock = useCallback(() => {
    showInfo(t('cart.not-enough-in-stock'))
  }, [showInfo, t])

  const notifySomeItemsOutOfStock = useCallback(() => {
    showInfo(t('cart.some-items-out-of-stock'))
  }, [showInfo, t])

  const notifyCartCreateError = useCallback(() => {
    showError(t('cart.cart-create-error'))
  }, [showError, t])

  const notifyItemFailedAdd = useCallback(() => {
    showError(t('cart.item-failed-add'))
  }, [showError, t])

  const notifyItemFailedAddWithReason = useCallback(
    (_status: string) => {
      showError(t('cart.item-failed-add'))
    },
    [showError, t]
  )

  const notifyCartUpdated = useCallback(
    (
      notificationType: NotificationTypeEnum,
      action: 'added' | 'removed',
      totalPrice: Price,
      productCount: number
    ) => {
      showUpdatedCart(notificationType, action, totalPrice, productCount)
    },
    [showUpdatedCart]
  )

  const notifyCartsMerged = useCallback(() => {
    showInfo(t('cart.carts-merged'))
  }, [showInfo, t])

  const notifyMergeFailed = useCallback(() => {
    showError(t('cart.merge-failed'))
  }, [showError, t])

  const notifyFallbackFailed = useCallback(() => {
    showError(t('cart.fallback-failed'))
  }, [showError, t])

  const notifyLoadFailed = useCallback(() => {
    showError(t('cart.load-failed'))
  }, [showError, t])

  const notifyCouponApplied = useCallback(() => {
    showInfo(t('coupon-codes.applied-successfully'))
  }, [showInfo, t])

  const notifyCouponAlreadyApplied = useCallback(() => {
    showError(t('coupon-codes.already-applied'))
  }, [showError, t])

  const notifyCouponApplyFailed = useCallback(
    (code: string) => {
      showError(t('coupon-codes.apply-failed', { code }))
    },
    [showError, t]
  )

  const notifyCouponRemoved = useCallback(() => {
    showInfo(t('coupon-codes.removed-successfully'))
  }, [showInfo, t])

  const notifyCouponRemoveFailed = useCallback(
    (code: string) => {
      showError(t('coupon-codes.remove-failed', { code }))
    },
    [showError, t]
  )

  const notifyStoreSelectionError = useCallback(
    (storeName: string) => {
      showError(t('cart.store-selection-error', { store: storeName }))
    },
    [showError, t]
  )

  return {
    notifyNotEnoughStock,
    notifySomeItemsOutOfStock,
    notifyCartCreateError,
    notifyItemFailedAdd,
    notifyItemFailedAddWithReason,
    notifyCartUpdated,
    notifyCartsMerged,
    notifyMergeFailed,
    notifyFallbackFailed,
    notifyLoadFailed,
    notifyCouponApplied,
    notifyCouponAlreadyApplied,
    notifyCouponApplyFailed,
    notifyCouponRemoved,
    notifyCouponRemoveFailed,
    notifyStoreSelectionError,
  }
}
